import { component } from '@diligentcorp/atlas-design-tokens/lens';
import type {
  LensPresets,
  LensTheme,
} from '@diligentcorp/atlas-theme-mui-lens';
import {
  Box,
  CircularProgress,
  Drawer,
  Link,
  Tab,
  Tabs,
  useTheme,
} from '@mui/material';
import React, { ReactNode, useState } from 'react';
import emptyStateIllustration from 'src/components/CategoryConfiguration/assets/atlas-empty-state.svg';
import {
  AuditLogColumn,
  AuditLogPaginatedTable,
} from 'src/components/atlas/molecules/AuditLogPaginatedTable';
import { EmptyState } from 'src/components/atlas/molecules/EmptyState';
import { AuditEntryType, useGetCategoriesAuditEntriesQuery } from 'src/graphql';
import { auditActionToLabel } from 'src/utils/auditActionToLabel';
import { formatDateTime } from 'src/utils/commonFunctions';

export interface CategoriesAuditLogDrawerProps {
  open: boolean;
  onClose: () => void;
  speakUpEnabled?: boolean;
  coiEnabled?: boolean;
  /**
   * Optional handler called when the user clicks a category-name link in the
   * audit table. Receives the full audit entry; the integrator decides what id
   * to use (e.g. `entry.data.categoryId`). When omitted, the category name
   * renders as plain text (no interactive element).
   */
  onCategoryClick?: (entry: AuditEntryType) => void;
}

const COLUMNS: AuditLogColumn[] = [
  { label: 'User' },
  { label: 'Category name', cellSx: { textDecoration: 'underline' } },
  { label: 'Event' },
  { label: 'Date' },
];

const PAGE_SIZE = 10;

const EMPTY_STATE = (
  <EmptyState
    title="No events logged"
    description="There are no events logged yet"
    illustration={
      <img
        src={emptyStateIllustration}
        alt=""
        aria-hidden="true"
        data-testid="empty-state-illustration"
        width={144}
        height={144}
      />
    }
  />
);

const ERROR_STATE = (
  <EmptyState
    title="Couldn't load audit log"
    description="Try closing and reopening the panel. If this keeps happening, contact support."
  />
);

const getCategoryName = (entry: AuditEntryType): string => {
  const data = (entry.data ?? {}) as {
    name?: string;
    newName?: string;
    oldName?: string;
  };
  return data.newName ?? data.oldName ?? data.name ?? 'Untitled';
};

const getUserName = (entry: AuditEntryType): string =>
  entry.User?.name ?? entry.User?.displayName ?? 'System';

/**
 * Audit log side drawer for the Categories Configuration page.
 *
 * Renders Atlas Lens styling — mount inside `<AtlasMuiThemeProvider>`.
 *
 * Built from Atlas's side-sheet primitives (`Header`, `Content`) so all
 * width/padding/typography resolve from the Atlas contract — no custom
 * `sx` overrides on the chrome.
 */
export const CategoriesAuditLogDrawer: React.FC<
  CategoriesAuditLogDrawerProps
> = ({
  open,
  onClose,
  speakUpEnabled = true,
  coiEnabled = true,
  onCategoryClick,
}) => {
  const { presets } = useTheme<LensTheme>();
  const SideSheetPresets = presets?.SideSheetPresets;
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={SideSheetPresets ? { ...SideSheetPresets.size.large.sx } : undefined}
      data-testid="audit-log-drawer"
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-labelledby': 'audit-log-title',
        },
      }}
    >
      {open ? (
        <DrawerBody
          onClose={onClose}
          speakUpEnabled={speakUpEnabled}
          coiEnabled={coiEnabled}
          onCategoryClick={onCategoryClick}
          SideSheetPresets={SideSheetPresets}
        />
      ) : null}
    </Drawer>
  );
};

type SideSheetPresetsShape = LensPresets['SideSheetPresets'];

interface DrawerBodyProps {
  onClose: () => void;
  speakUpEnabled: boolean;
  coiEnabled: boolean;
  onCategoryClick?: (entry: AuditEntryType) => void;
  SideSheetPresets?: SideSheetPresetsShape;
}

const DrawerBody: React.FC<DrawerBodyProps> = ({
  onClose,
  speakUpEnabled,
  coiEnabled,
  onCategoryClick,
  SideSheetPresets,
}) => {
  const neitherEnabled = !speakUpEnabled && !coiEnabled;
  const [activeTab, setActiveTab] = useState<'speak-up' | 'coi'>(
    speakUpEnabled ? 'speak-up' : 'coi',
  );
  const { data, isLoading, isError } = useGetCategoriesAuditEntriesQuery(
    { isCOI: activeTab === 'coi' },
    { enabled: !neitherEnabled },
  );

  const entries: AuditEntryType[] =
    (data?.categoriesAuditEntries as AuditEntryType[] | undefined) ?? [];

  const renderRow = (entry: AuditEntryType): ReactNode[] => {
    const name = getCategoryName(entry);
    const nameCell = onCategoryClick ? (
      <Link
        component="button"
        type="button"
        onClick={() => onCategoryClick(entry)}
        sx={{ textAlign: 'left' }}
      >
        {name}
      </Link>
    ) : (
      name
    );
    return [
      getUserName(entry),
      nameCell,
      auditActionToLabel(entry.action),
      entry.createdAt ? formatDateTime(new Date(entry.createdAt)) : '',
    ];
  };

  let body: ReactNode;
  if (isLoading) {
    body = (
      <Box
        data-testid="audit-log-loading"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  } else if (isError) {
    body = ERROR_STATE;
  } else {
    body = (
      <AuditLogPaginatedTable
        entries={entries}
        columns={COLUMNS}
        renderRow={renderRow}
        pageSize={PAGE_SIZE}
        emptyState={EMPTY_STATE}
        ariaLabel={
          activeTab === 'coi'
            ? 'Conflict of interest audit log'
            : 'Speak up audit log'
        }
      />
    );
  }

  const Header = SideSheetPresets?.components?.Header;
  const Content = SideSheetPresets?.components?.Content;

  return (
    <>
      {Header ? (
        <Header
          variant="default"
          onClose={onClose}
          title="Audit log"
          componentProps={{
            closeButton: { 'aria-label': 'Close audit log' },
            title: { component: 'h2', id: 'audit-log-title' },
          }}
        />
      ) : null}
      {neitherEnabled ? (
        Content ? (
          <Content ariaLabel="Audit log entries">
            <EmptyState
              title="No modules enabled"
              description="No modules are enabled for this organisation."
            />
          </Content>
        ) : (
          <Box>
            <EmptyState
              title="No modules enabled"
              description="No modules are enabled for this organisation."
            />
          </Box>
        )
      ) : (
        <>
          <Box
            sx={{
              marginTop: component.sideSheet.gap.value,
              paddingLeft: component.sideSheet.paddingLeft.value,
              paddingRight: component.sideSheet.paddingRight.value,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(_event, value) => setActiveTab(value)}
              aria-label="Audit log category type"
            >
              {speakUpEnabled && <Tab value="speak-up" label="Speak up" />}
              {coiEnabled && <Tab value="coi" label="Conflict of interest" />}
            </Tabs>
          </Box>
          {Content ? (
            <Content ariaLabel="Audit log entries">{body}</Content>
          ) : (
            <Box>{body}</Box>
          )}
        </>
      )}
    </>
  );
};
